import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import FilterWrapper from 'v2/components/GDS/Filter';
import { DatePickerInput } from 'pepsico-ds';
import DownloadMenu from '../../OfflineDownload/DownloadMenu';
import { WeekPickerFilter } from './WeekPickerFilter';
import { constants } from '../../../constants/Constants';
import { ThemeContext } from '../../../context/ThemeContext';
import { FilterContext } from '../FilterProvider';
import {
  getFrequencyConfig,
  convertToFrequencyOptions,
  getWeekConfig,
  selectFrequencyWithPreference,
} from 'utils/frequencyConfigUtils';
import { getWeekRange } from '../helper';
import { startOfWeek } from 'date-fns';
import { useFrequencyDateLogic } from '../hook/useFrequencyDateLogic';
import { useFilterV2 } from '../../../hooks/useFilterV2';
import store from '@store/store';

// Global styles (Unchanged)
const DatePickerGlobalStyle = createGlobalStyle`
  .pepsico-datepicker-portal,
  [class*='DatePicker'],
  [class*='datepicker-portal'],
  [id*='datepicker-portal'],
  [role='dialog'][aria-label*='calendar'],
  [role='dialog'][aria-label*='date'] {
    z-index: 9999 !important;
    position: fixed !important;
  }
`;

// Styled Components (Unchanged)
const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  z-index: ${({ zIndex }) => zIndex || 10};

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DatePickerWrapper = styled.div`
  position: relative;
  z-index: 1000;

  /* Ensure the date picker popup appears above other elements */
  & .pds-datepicker-portal,
  & [class*='datepicker'],
  & [class*='calendar'] {
    z-index: 9999 !important;
  }
`;

const SkeletonLoader = styled.div`
  width: 100%;
  height: 40px;
  background: ${({ theme }) =>
    theme === 'dark'
      ? 'linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%)'
      : 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)'};
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const DownloadWrapper = styled.div`
  @media (max-width: 1024px) {
    display: none;
  }
`;

/**
 * Safely retrieves a valid week start day (0–6), defaulting to Monday (1).
 * @param {number|string|null|undefined} startDay - Week start day from config (0–6 or string like 'monday')
 * @returns {number} Valid week start day (0–6)
 */
const getSafeWeekStartDay = (startDay) => {
  const dayMap = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  // Numeric input validation
  if (Number.isInteger(startDay) && startDay >= 0 && startDay <= 6) {
    return startDay;
  }

  // String input conversion
  if (typeof startDay === 'string') {
    const dayNumber = dayMap[startDay.trim().toLowerCase()];
    if (dayNumber !== undefined) {
      return dayNumber;
    }
  }

  // Fallback to Monday
  return 1;
};

/**
 * Unique filter IDs for Redux state management (Filter Slice V2)
 *
 * The FrequencyFilter component saves its state to Redux using these unique IDs:
 * - FREQUENCY: Stores the selected frequency type (Weekly/Monthly/Yearly) with config
 * - SELECTED_WEEK: Stores the selected week start date and week range (for Weekly frequency)
 * - SELECTED_MONTH: Stores the selected month date and month/year values (for Monthly frequency)
 * - SELECTED_YEAR: Stores the selected year (used across all frequency types)
 *
 * Redux state structure for each filter:
 * {
 *   filterId: {
 *     selectedValue: <value>,  // The actual data (object or primitive)
 *     enabled: <boolean>,      // true when filter is active/available
 *     loading: <boolean>       // true during initialization/updates
 *   }
 * }
 *
 * Enabled state logic:
 * - FREQUENCY: enabled when options are available
 * - SELECTED_WEEK: enabled only in Weekly mode
 * - SELECTED_MONTH: enabled only in Monthly mode
 * - SELECTED_YEAR: always enabled (used in all modes)
 */
const FILTER_IDS = {
  FREQUENCY: 'frequency-filter',
  SELECTED_WEEK: 'frequency-selected-week',
  SELECTED_MONTH: 'frequency-selected-month',
  SELECTED_YEAR: 'frequency-selected-year',
  START_UTC_DATE: 'startUTCDate',
  END_UTC_DATE: 'endUTCDate',
  YEAR: 'year', // Year value when frequency is Yearly, otherwise null
};

/**
 * Reusable Frequency Filter component with frequency selector and date picker
 */
const FrequencyFilter = ({ goLiveMonth, goLiveYear }) => {
  const { handleYtdModeOn, handleYtdModeOff, setDateRangeAndUTC } =
    useFrequencyDateLogic({ goLiveYear, goLiveMonth });
  const { mode: themeMode } = useContext(ThemeContext);
  const isMFE = store.getState().app.isMFE;
  // Redux filter management
  const { updateFilterValue, updateFilterEnabled, updateFilterLoading } =
    useFilterV2();
  const {
    setYTDCheckbox,
    selectedProduct,
    selectedProgram,
    selectedYear,
    selectedDate,
    setSelectedDate,
    setSelectedYear,
    setSelectedStartDate,
    setStartUTCDate,
    setSelectedEndDate,
    setEndUTCDate,
    selectedMarket,
    selectedCustomer,
    selectedModel,
    currentTabName,
    currentSubTabName,
    filtersData,
    selectedFrequency,
    setSelectedFrequency,
    setSelectedFrequencyConfig,
  } = useContext(FilterContext);

  // Determine week picker restrictions based on current tab context
  const weekPickerRestrictions = useMemo(() => {
    const isCustomerBehaviorTab = currentSubTabName === 'Customer Behavior';
    return isCustomerBehaviorTab
      ? { restrictToLastNWeeks: 5, defaultToMostRecentWeek: true }
      : { restrictToLastNWeeks: null, defaultToMostRecentWeek: false };
  }, [currentSubTabName]);

  const currentMode = themeMode;

  // --- Frequency Config Logic (Unchanged) ---
  const availableFrequencyConfigs = useMemo(() => {
    // ... (logic unchanged)
    if (
      !filtersData ||
      !selectedProgram?.programId ||
      !selectedProduct?.productId ||
      !currentTabName
    ) {
      // Return empty array if required data not available
      // convertToFrequencyOptions will provide default fallback
      return [];
    }
    return getFrequencyConfig({
      filtersData,
      programId: selectedProgram.programId,
      productId: selectedProduct.productId,
      modelId: selectedModel?.id || null,
      tabName: currentTabName,
      subTabName: currentSubTabName,
    });
  }, [
    filtersData,
    selectedProgram?.programId,
    selectedProduct?.productId,
    selectedModel?.id,
    currentTabName,
    currentSubTabName,
  ]);

  // Convert frequency configs to options format for FilterWrapper
  const frequencyOptions = useMemo(() => {
    const options = convertToFrequencyOptions(availableFrequencyConfigs);

    // Sort options in the order: Weekly, Monthly, Yearly
    const order = { Weekly: 0, Monthly: 1, Yearly: 2 };
    return options.sort((a, b) => {
      const orderA = order[a.id] !== undefined ? order[a.id] : 999;
      const orderB = order[b.id] !== undefined ? order[b.id] : 999;
      return orderA - orderB;
    });
  }, [availableFrequencyConfigs]);
  // --- End Frequency Config Logic ---

  // Selected week state for CustomWeekPicker
  const [selectedWeek, setSelectedWeek] = useState(new Date());

  // Track context to detect when program/product/tab changes (should reset week)
  // vs when user just selects a different week (should NOT reset)
  const [lastInitContext, setLastInitContext] = useState(null);

  // Initialize Redux filter states on mount (run only once)
  useEffect(() => {
    // Initialize all frequency filters as enabled
    updateFilterEnabled(FILTER_IDS.FREQUENCY, true);
    updateFilterEnabled(FILTER_IDS.SELECTED_WEEK, true);
    updateFilterEnabled(FILTER_IDS.SELECTED_MONTH, true);
    updateFilterEnabled(FILTER_IDS.SELECTED_YEAR, true);
    updateFilterEnabled(FILTER_IDS.START_UTC_DATE, true);
    updateFilterEnabled(FILTER_IDS.END_UTC_DATE, true);
    updateFilterEnabled(FILTER_IDS.YEAR, true);

    // Set initial loading to false
    updateFilterLoading(FILTER_IDS.FREQUENCY, false);
    updateFilterLoading(FILTER_IDS.SELECTED_WEEK, false);
    updateFilterLoading(FILTER_IDS.SELECTED_MONTH, false);
    updateFilterLoading(FILTER_IDS.SELECTED_YEAR, false);
    updateFilterLoading(FILTER_IDS.START_UTC_DATE, false);
    updateFilterLoading(FILTER_IDS.END_UTC_DATE, false);
    updateFilterLoading(FILTER_IDS.YEAR, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save initial frequency and year to Redux when first set
  useEffect(() => {
    if (selectedFrequency?.id && selectedYear) {
      // Save frequency
      updateFilterValue(FILTER_IDS.FREQUENCY, {
        id: selectedFrequency.id,
        label: selectedFrequency.label,
        config: selectedFrequency.config || null,
      });
      // Save year
      updateFilterValue(FILTER_IDS.SELECTED_YEAR, selectedYear);
      // Set year filter based on frequency (only set value for Yearly)
      updateFilterValue(
        FILTER_IDS.YEAR,
        selectedFrequency.id === 'Yearly' ? selectedYear : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFrequency?.id, selectedYear]);

  // --- Main YTD useEffect (Ported from CustomDatePicker) ---
  useEffect(() => {
    // This logic runs when 'selectedFrequency?.id' or 'selectedYear' changes
    if (selectedFrequency?.id === 'Weekly') {
      setYTDCheckbox(false);
      return;
    }

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const isShipmentVisibility =
      selectedProduct?.productId === constants?.products?.ShipmentVisibility;

    const params = {
      selectedYear,
      goLiveYear,
      goLiveMonth,
      currentYear,
      currentMonth,
      isShipmentVisibility,
    };

    if (selectedFrequency?.id === 'Yearly') {
      handleYtdModeOn(params);
    } else if (selectedFrequency?.id === 'Monthly') {
      handleYtdModeOff(params);
    }
  }, [
    selectedYear,
    selectedProduct,
    goLiveYear,
    goLiveMonth,
    selectedMarket,
    selectedCustomer,
    selectedModel,
    // selectedSector, // Missing from context, assuming it's not critical
    handleYtdModeOn,
    handleYtdModeOff,
    selectedFrequency,
  ]);

  // --- Week/Date Helper Logic ---
  const weekConfig = useMemo(() => {
    const isSuccessReport = currentTabName?.toLowerCase() === 'success report';
    return getWeekConfig(
      availableFrequencyConfigs,
      isSuccessReport ? selectedMarket?.marketId : null
    );
  }, [availableFrequencyConfigs, currentTabName, selectedMarket?.marketId]);

  // Calculate default week for weekly picker
  const calculateDefaultWeek = useCallback(() => {
    const today = new Date();
    const weekStartDay = getSafeWeekStartDay(weekConfig?.startDay);

    // Always default to current week for Weekly frequency or when explicitly requested
    if (
      selectedFrequency?.id === 'Weekly' ||
      weekPickerRestrictions.defaultToMostRecentWeek
    ) {
      return startOfWeek(today, { weekStartsOn: weekStartDay });
    }

    if (selectedFrequency?.id === 'Yearly') {
      // YTD mode
      return selectedYear === today.getFullYear()
        ? startOfWeek(today, { weekStartsOn: weekStartDay })
        : startOfWeek(new Date(selectedYear, 11, 31), {
            weekStartsOn: weekStartDay,
          });
    }

    // Month mode
    const currentMonth = today?.getMonth();
    if (
      selectedYear === today.getFullYear() &&
      currentMonth === today.getMonth()
    ) {
      return startOfWeek(today, { weekStartsOn: weekStartDay });
    }

    const lastDayOfMonth = new Date(selectedYear, currentMonth + 1, 0);
    let computedStartOfWeek = startOfWeek(lastDayOfMonth, {
      weekStartsOn: weekStartDay,
    });

    if (
      currentMonth === 11 &&
      computedStartOfWeek.getFullYear() !== selectedYear
    ) {
      computedStartOfWeek = startOfWeek(new Date(selectedYear, 11, 31), {
        weekStartsOn: weekStartDay,
      });
    }

    return computedStartOfWeek;
  }, [
    selectedFrequency?.id,
    selectedYear,
    weekPickerRestrictions.defaultToMostRecentWeek,
    weekConfig?.startDay,
  ]);

  // Helper: Get selectable dates based on frequency and go-live constraints
  const getSelectableDates = useCallback(() => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const minYear = goLiveYear || currentYear - 5;
    const dates = [];

    if (selectedFrequency?.id === 'Yearly') {
      // Generate year options
      for (let year = minYear; year <= currentYear; year++) {
        dates.push(new Date(year, 0, 1));
      }
    } else if (selectedFrequency?.id === 'Monthly') {
      // Generate month options
      for (let year = minYear; year <= currentYear; year++) {
        const startMonth = year === minYear ? goLiveMonth || 0 : 0;
        const endMonth = year === currentYear ? currentMonth : 11;
        for (let month = startMonth; month <= endMonth; month++) {
          dates.push(new Date(year, month, 1));
        }
      }
    }
    // For Weekly, no restriction on selectable dates
    return dates;
  }, [selectedFrequency?.id, goLiveYear, goLiveMonth]);

  // Auto-adjust selected frequency based on available options
  // Uses a simple debounce to wait for context (program, options) to stabilize
  useEffect(() => {
    // Define the logic to run after the debounce
    const applyFrequencySelection = () => {
      // Early returns for invalid states
      if (frequencyOptions.length === 0) {
        updateFilterEnabled(FILTER_IDS.FREQUENCY, false);
        updateFilterLoading(FILTER_IDS.FREQUENCY, false);
        return;
      }
      if (
        !selectedProgram?.programId ||
        !selectedProduct?.productId ||
        !currentTabName
      ) {
        updateFilterLoading(FILTER_IDS.FREQUENCY, true);
        return;
      }

      // Enable frequency filter when options are available
      updateFilterEnabled(FILTER_IDS.FREQUENCY, true);
      updateFilterLoading(FILTER_IDS.FREQUENCY, false);

      // Select frequency: keep current if available, otherwise default to Monthly
      const selectedOption = selectFrequencyWithPreference(
        frequencyOptions,
        selectedFrequency
      );

      // Update if the selected option is different from current
      if (selectedOption && selectedOption.id !== selectedFrequency?.id) {
        setSelectedFrequency(selectedOption);
        setSelectedFrequencyConfig(selectedOption.config || null);
      }
    };

    // Set a timer. Any new change to the dependencies will clear the old
    // timer (in the cleanup) and set a new one. This ensures the
    // logic only runs 300ms *after* the *last* change has occurred.
    const timerId = setTimeout(applyFrequencySelection, 300);

    // Cleanup function: This runs on every re-render (before the next
    // effect) or on unmount, clearing the pending timer.
    return () => {
      clearTimeout(timerId);
    };
  }, [
    // Dependencies that trigger the re-evaluation
    frequencyOptions,
    selectedProgram?.programId,
    selectedProduct?.productId,
    currentTabName,
    currentSubTabName,
    selectedFrequency,

    // Setters used inside the effect
    setSelectedFrequency,
    setSelectedFrequencyConfig,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // Initialize weekly picker with saved data or defaults
  // Resets to default week when program/product/tab changes, but NOT on user week selection
  useEffect(() => {
    if (selectedFrequency?.id !== 'Weekly') {
      // Disable week filter when not in Weekly mode
      updateFilterEnabled(FILTER_IDS.SELECTED_WEEK, false);
      setLastInitContext(null);
      return;
    }

    // Build current context key to detect meaningful changes
    const currentContext = `${selectedProgram?.programId}-${selectedProduct?.productId}-${currentTabName}-${currentSubTabName}`;

    // Skip if context hasn't changed (user just selected a different week)
    if (lastInitContext === currentContext) {
      return;
    }

    // Enable week filter in Weekly mode
    updateFilterEnabled(FILTER_IDS.SELECTED_WEEK, true);
    updateFilterLoading(FILTER_IDS.SELECTED_WEEK, true);

    const weekStartDay = getSafeWeekStartDay(weekConfig?.startDay);

    // Calculate default week
    const defaultWeek = calculateDefaultWeek();
    const weekRange = getWeekRange(defaultWeek, weekStartDay);

    setSelectedWeek(defaultWeek);
    setSelectedStartDate(new Date(weekRange.start));
    setStartUTCDate(weekRange.start);
    setSelectedEndDate(new Date(weekRange.end));
    setEndUTCDate(weekRange.end);

    // Save to Redux
    updateFilterValue(FILTER_IDS.SELECTED_WEEK, {
      weekStart: defaultWeek.toISOString(),
      weekRange: {
        start: weekRange.start,
        end: weekRange.end,
      },
    });
    // Sync UTC dates to Redux
    updateFilterLoading(FILTER_IDS.START_UTC_DATE, true);
    updateFilterLoading(FILTER_IDS.END_UTC_DATE, true);
    updateFilterValue(FILTER_IDS.START_UTC_DATE, weekRange.start);
    updateFilterValue(FILTER_IDS.END_UTC_DATE, weekRange.end);
    updateFilterLoading(FILTER_IDS.START_UTC_DATE, false);
    updateFilterLoading(FILTER_IDS.END_UTC_DATE, false);
    updateFilterLoading(FILTER_IDS.SELECTED_WEEK, false);

    // Store context to detect future changes
    setLastInitContext(currentContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFrequency?.id,
    selectedProgram?.programId,
    selectedProduct?.productId,
    currentTabName,
    currentSubTabName,
    weekConfig?.startDay,
    calculateDefaultWeek,
  ]);

  // Handle frequency change - update both frequency and config in context
  const handleFrequencyChange = (option) => {
    if (option && option.id) {
      setSelectedFrequency(option);
      setSelectedFrequencyConfig(option.config || null);

      // Save to Redux (synchronously update all relevant states)
      updateFilterValue(FILTER_IDS.FREQUENCY, {
        id: option.id,
        label: option.label,
        config: option.config || null,
      });
      updateFilterEnabled(FILTER_IDS.FREQUENCY, true);
      updateFilterLoading(FILTER_IDS.FREQUENCY, false);

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      const isShipmentVisibility =
        selectedProduct?.productId === constants?.products?.ShipmentVisibility;

      if (option.id === 'Weekly') {
        setYTDCheckbox(false);
        const weekStartDay = getSafeWeekStartDay(weekConfig?.startDay);
        const currentWeekStart = startOfWeek(currentDate, {
          weekStartsOn: weekStartDay,
        });
        const weekRange = getWeekRange(currentWeekStart, weekStartDay);

        setSelectedWeek(currentWeekStart);
        setSelectedStartDate(new Date(weekRange.start));
        setStartUTCDate(weekRange.start);
        setSelectedEndDate(new Date(weekRange.end));
        setEndUTCDate(weekRange.end);
        setSelectedDate(currentWeekStart);
        setSelectedYear(currentYear);

        // Save week to Redux
        updateFilterValue(FILTER_IDS.SELECTED_WEEK, {
          weekStart: currentWeekStart.toISOString(),
          weekRange: {
            start: weekRange.start,
            end: weekRange.end,
          },
        });
        // Sync UTC dates to Redux
        updateFilterLoading(FILTER_IDS.START_UTC_DATE, true);
        updateFilterLoading(FILTER_IDS.END_UTC_DATE, true);
        updateFilterValue(FILTER_IDS.START_UTC_DATE, weekRange.start);
        updateFilterValue(FILTER_IDS.END_UTC_DATE, weekRange.end);
        updateFilterLoading(FILTER_IDS.START_UTC_DATE, false);
        updateFilterLoading(FILTER_IDS.END_UTC_DATE, false);
        updateFilterEnabled(FILTER_IDS.SELECTED_WEEK, true);
        updateFilterEnabled(FILTER_IDS.SELECTED_MONTH, false);
        updateFilterValue(FILTER_IDS.SELECTED_YEAR, currentYear);
        updateFilterEnabled(FILTER_IDS.SELECTED_YEAR, true);
        // Set year to null for non-Yearly frequency
        updateFilterValue(FILTER_IDS.YEAR, null);
      } else if (option.id === 'Monthly') {
        setYTDCheckbox(false);
        const monthDate = new Date(currentYear, currentMonth, 1);
        setSelectedDate(monthDate);
        setSelectedYear(currentYear);

        setDateRangeAndUTC(monthDate, monthDate, isShipmentVisibility);

        // Save month and year to Redux
        updateFilterValue(FILTER_IDS.SELECTED_MONTH, {
          date: monthDate.toISOString(),
          month: currentMonth,
          year: currentYear,
        });
        updateFilterEnabled(FILTER_IDS.SELECTED_MONTH, true);
        updateFilterEnabled(FILTER_IDS.SELECTED_WEEK, false);
        updateFilterValue(FILTER_IDS.SELECTED_YEAR, currentYear);
        updateFilterEnabled(FILTER_IDS.SELECTED_YEAR, true);
        // Set year to null for non-Yearly frequency
        updateFilterValue(FILTER_IDS.YEAR, null);
      } else if (option.id === 'Yearly') {
        setYTDCheckbox(true);
        const yearDate = new Date(currentYear, 0, 1);
        const endYearDate = new Date(currentYear, currentMonth, 1);

        setSelectedDate(endYearDate);
        setSelectedYear(currentYear);

        setDateRangeAndUTC(yearDate, endYearDate, isShipmentVisibility);

        // Save year to Redux
        updateFilterValue(FILTER_IDS.SELECTED_YEAR, currentYear);
        updateFilterEnabled(FILTER_IDS.SELECTED_YEAR, true);
        updateFilterEnabled(FILTER_IDS.SELECTED_WEEK, false);
        updateFilterEnabled(FILTER_IDS.SELECTED_MONTH, false);
        // Set year value for Yearly frequency
        updateFilterValue(FILTER_IDS.YEAR, currentYear);
      }
    }
  };

  // Handle week change for CustomWeekPicker
  const handleWeekChange = (weekStartDate) => {
    if (!weekStartDate) {
      return;
    }

    const weekStartDay = getSafeWeekStartDay(weekConfig?.startDay);
    const weekRange = getWeekRange(weekStartDate, weekStartDay);

    const startOfWeekDate = new Date(weekRange.start);
    const endOfWeekDate = new Date(weekRange.end);

    // Update state
    setSelectedWeek(weekStartDate);
    setSelectedStartDate(startOfWeekDate);
    setStartUTCDate(weekRange.start);
    setSelectedEndDate(endOfWeekDate);
    setEndUTCDate(weekRange.end);
    setSelectedDate(startOfWeekDate);
    setSelectedYear(startOfWeekDate.getFullYear());

    // Save to Redux
    updateFilterLoading(FILTER_IDS.SELECTED_WEEK, true);
    updateFilterValue(FILTER_IDS.SELECTED_WEEK, {
      weekStart: weekStartDate.toISOString(),
      weekRange: {
        start: weekRange.start,
        end: weekRange.end,
      },
    });
    updateFilterValue(FILTER_IDS.SELECTED_YEAR, startOfWeekDate.getFullYear());
    // Sync UTC dates to Redux
    updateFilterLoading(FILTER_IDS.START_UTC_DATE, true);
    updateFilterLoading(FILTER_IDS.END_UTC_DATE, true);
    updateFilterValue(FILTER_IDS.START_UTC_DATE, weekRange.start);
    updateFilterValue(FILTER_IDS.END_UTC_DATE, weekRange.end);
    updateFilterLoading(FILTER_IDS.START_UTC_DATE, false);
    updateFilterLoading(FILTER_IDS.END_UTC_DATE, false);
    updateFilterLoading(FILTER_IDS.SELECTED_WEEK, false);
  };

  // Handle date change (for Monthly/Yearly)
  const handleDateChange = (date) => {
    if (!date) {
      return;
    }
    const selectedDateObj = new Date(date);
    const year = selectedDateObj.getFullYear();
    const month = selectedDateObj.getMonth();

    const isShipmentVisibility =
      selectedProduct?.productId === constants?.products?.ShipmentVisibility;

    if (selectedFrequency?.id === 'Monthly') {
      setDateRangeAndUTC(
        selectedDateObj,
        selectedDateObj,
        isShipmentVisibility
      );

      setSelectedDate(selectedDateObj);

      // Save to Redux
      updateFilterLoading(FILTER_IDS.SELECTED_MONTH, true);
      updateFilterValue(FILTER_IDS.SELECTED_MONTH, {
        date: selectedDateObj.toISOString(),
        month: month,
        year: year,
      });
      updateFilterValue(FILTER_IDS.SELECTED_YEAR, year);
      updateFilterLoading(FILTER_IDS.SELECTED_MONTH, false);
    } else if (selectedFrequency?.id === 'Yearly') {
      // Only update selectedYear - the useEffect will handle date range updates
      setSelectedYear(year); // <-- This triggers the YTD useEffect (line 388)

      // Save to Redux
      updateFilterLoading(FILTER_IDS.SELECTED_YEAR, true);
      updateFilterValue(FILTER_IDS.SELECTED_YEAR, year);
      // Update year filter value for Yearly frequency
      updateFilterValue(FILTER_IDS.YEAR, year);
      updateFilterLoading(FILTER_IDS.SELECTED_YEAR, false);
    }
  };

  // --- Render Logic ---
  const getDatePickerType = () => {
    if (!selectedFrequency?.id) {
      return null;
    }
    if (selectedFrequency.id === 'Monthly') {
      return 'month';
    }
    if (selectedFrequency.id === 'Yearly') {
      return 'year';
    }
    return 'daily';
  };

  const availableDates = getSelectableDates();

  return (
    <>
      <DatePickerGlobalStyle />
      <Container data-testid="frequency-filter-container">
        {/* Frequency Filter */}
        <FilterGroup zIndex={20}>
          <FilterWrapper
            options={frequencyOptions}
            value={selectedFrequency}
            onChange={handleFrequencyChange}
            selection="single"
            placeholder="Frequency"
            maxWidth="100%"
            controlled={true}
            data-testid="frequency-filter"
          />
        </FilterGroup>

        {/* Date Picker Input - Conditional rendering based on frequency */}
        <FilterGroup>
          <DatePickerWrapper>
            {!selectedFrequency?.id ? (
              <SkeletonLoader theme={currentMode} />
            ) : selectedFrequency.id === 'Weekly' ? (
              <WeekPickerFilter
                key={`week-picker-${weekPickerRestrictions.restrictToLastNWeeks || 'all'}`}
                portalId={'b2b-week-picker'}
                selectedWeek={selectedWeek}
                setSelectedWeek={handleWeekChange}
                goLiveDate={
                  new Date(
                    goLiveYear || new Date().getFullYear() - 5,
                    goLiveMonth || 0,
                    1
                  )
                }
                weekStartsOn={getSafeWeekStartDay(weekConfig?.startDay)}
                disabled={false}
                restrictToLastNWeeks={
                  weekPickerRestrictions.restrictToLastNWeeks
                }
              />
            ) : (
              <DatePickerInput
                pickerType={getDatePickerType()}
                selected={
                  selectedFrequency?.id === 'Yearly'
                    ? new Date(selectedYear, 0, 1) // Year picker uses selectedYear
                    : selectedDate // Month picker uses selectedDate
                }
                setSelected={handleDateChange}
                startYear={goLiveYear || new Date().getFullYear() - 5}
                endYear={new Date().getFullYear()}
                showClearIcon={false}
                monthPickerMenu={
                  selectedFrequency?.id === 'Monthly'
                    ? { onlySelectFrom: availableDates }
                    : undefined
                }
                yearPickerMenu={
                  selectedFrequency?.id === 'Yearly'
                    ? { onlySelectFrom: availableDates }
                    : undefined
                }
                menuPosition="bottom-left"
                placeholder={
                  selectedFrequency?.id === 'Monthly'
                    ? 'Select month'
                    : 'Select year'
                }
                data-testid="date-picker-input"
                style={{ maxWidth: '320px' }}
              />
            )}
          </DatePickerWrapper>
        </FilterGroup>

        {/* Download Menu - Hidden on mobile/tablet */}
        {!isMFE && (
          <DownloadWrapper data-html2canvas-ignore>
            <DownloadMenu theme={currentMode} />
          </DownloadWrapper>
        )}
      </Container>
    </>
  );
};

export default FrequencyFilter;

// Export FILTER_IDS for use in other components
export { FILTER_IDS as FREQUENCY_FILTER_IDS };
